import { StoreState } from '@interfaces/stores';
import {
  FormGroupAssociatedData,
  learningCenterService
} from '@services/domain-factories/learningCenter';
import { createStoreWithSelector } from '@utils/zustand/createStoreWithSelector';
import { createUseStore } from '@utils/zustand/createUseStore';
import { initOnce } from '@utils/zustand/initOnce';
import { FormGroup } from '@vincerosoft/assessfluence-types/build/src/types/form-groups/form-group.interface';

const initialValue: StoreState['learningCenter'] = {
  tags: [],
  categories: [],
  byFormGroupId: {},
  isInitialized: false,
  loading: false,
  inFlightInit: null
};

type Actions = {
  getTaxonomyData: (formGroups: FormGroup[]) => Promise<{
    tags: StoreState['learningCenter']['tags'];
    categories: StoreState['learningCenter']['categories'];
  }>;
  initLearningCenter: () => Promise<void>;
  handleFormGroupUpdate: (
    formGroupId: string,
    campaignId: string,
    updatedData: FormGroupAssociatedData['byCampaignId'][string]
  ) => void;
};

export const learningCenterStore = createStoreWithSelector<
  StoreState['learningCenter'] & Actions
>()((set, get) => ({
  ...initialValue,

  getTaxonomyData: async (formGroups: FormGroup[]) => {
    const { tags, categories } = await learningCenterService.getTaxonomyData(formGroups);
    set({ tags, categories });
    return { tags, categories };
  },

  initLearningCenter: () =>
    initOnce(
      'isInitialized',
      set,
      get,
      () => learningCenterService.loadLearningCenterData(),
      (results) => set({ ...results, isInitialized: true })
    ),

  handleFormGroupUpdate: (formGroupId, campaignId, updatedData) => {
    set((state) => {
      const currentFormGroup = state.byFormGroupId?.[formGroupId];
      const currentCampaignData = currentFormGroup?.byCampaignId?.[campaignId];

      return {
        byFormGroupId: {
          ...(state.byFormGroupId || {}),
          [formGroupId]: {
            ...(currentFormGroup || {}),
            byCampaignId: {
              ...(currentFormGroup?.byCampaignId || {}),
              [campaignId]: {
                ...(currentCampaignData || {}),
                ...updatedData
              }
            }
          }
        }
      };
    });
  }
}));

export const useLearningCenterStore = createUseStore(learningCenterStore);
